import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {isCellSelected as isCellSelectedHelper} from 'notebook/tables/core-ui/TableRowUtils';
import {getErrorOrWarningFromCell} from 'notebook/tables/errors/CellErrorGetters';
import {TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell, EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {computeErrorState} from 'notebook/tables/store/logic/TableCellLogic';
import {computeIsLastColumn, computeIsLastRow} from 'notebook/tables/store/logic/TableDimensionsLogic';
import {computeIsHeaderCell, computeRowCellColors} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeVirtualRowIndex} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {RowModel} from 'notebook/tables/store/models/RowModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {
  CellTableModel,
  createCellTableSelector,
} from 'notebook/tables/store/selectors/cellModel/CellTableSelectors';
import {
  createDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {isTextEditableType} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * This contains volatile values, or values that frequently changes due to evaluation or renders,
 * from the RowModel or TableModel that are only relevant to this single cell.
 *
 * Everything inside here will be deep-compare memoized.
 */
interface VolatileCellModel {
  cell: Cell;
  evaluatedCell: EvaluatedCell;
}

/**
 * This model is what is passed down to the components.
 * This can contain data that is derived from the RowModel, TableModel, or TableEditableStore.
 */
export interface CellModel extends VolatileCellModel {
  rowData: RowData | null;
  columnConstraint: ColumnConstraint | null;
  error: EvaluatedCellError | undefined;
  errorState: TriangleState;

  /**
   * Whether this cell renders as a header cell: it is in the header row, or the first column of a
   * table with a header column. Always false for single-input cells.
   */
  isHeaderCell: boolean;
  /** Formula highlight class for this cell, when provided on table props. */
  highlightClass?: string;

  /**
   * Whether this cell is locked. Derived from the table lock state, the cell's
   * evaluated lock state, and the row's archive status.
   */
  isCellLocked: boolean;
  /**
   * Whether this cell falls within the current selection.
   */
  isCellSelected: boolean;
  /**
   * Whether this cell is the focal (double-selected) cell of the selection.
   */
  isCellDblSelected: boolean;
  /**
   * Whether this cell is the actively edited cell.
   */
  hasActiveEdit: boolean;

  /**
   * Whether this cell renders in the table's last row. Reflects actual data bounds, not ghost
   * cells. A merged cell renders at the first row of its merge box, so it is only the last row
   * when that box starts on the table's last row.
   */
  isLastRow: boolean;
  /**
   * Whether this cell renders in the table's last column. Reflects actual data bounds, not ghost
   * cells.
   */
  isLastColumn: boolean;

  /**
   * The table-level values this cell renders with, relayed from the {@link TableModel} so that
   * rendering a cell only needs the cell model.
   */
  table: CellTableModel;
}

const createVolatileCellModelSelector = (
  rowSelector: (store: TableEditableStore) => RowModel,
  columnIndex: number
): ((store: TableEditableStore) => VolatileCellModel) =>
  createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => rowSelector(store).row[columnIndex], (store) => rowSelector(store).evaluatedRow[columnIndex]],
    (cell, evaluatedCell) => ({cell, evaluatedCell})
  );

export const createCellModelSelector = (
  rowSelector: (store: TableEditableStore) => RowModel,
  columnIndex: number,
  rowIndex: number,
  tableSelector: (store: TableEditableStore) => TableModel
): ((store: TableEditableStore) => CellModel) =>
  createTableStoreSelector(
    {
      volatileCellModel: createVolatileCellModelSelector(rowSelector, columnIndex),
      rowData: (store) => rowSelector(store).rowData,
      columnConstraint: (store) => rowSelector(store).columnConstraints[columnIndex],
      isTombstoned: (store) => rowSelector(store).rowProperties.isTombstoned,
      selectedColIndexMin: (store) => rowSelector(store).selectedColIndexMin,
      selectedColIndexMax: (store) => rowSelector(store).selectedColIndexMax,
      selectedColIndexActive: (store) => rowSelector(store).selectedColIndexActive,
      isTableLocked: (store) => rowSelector(store).table.isTableLocked,
      hasActiveCell: (store) =>
        Boolean(
          tableSelector(store).props.multiEditing &&
          tableSelector(store).state.multiSelIndex?.[0] === rowIndex
        ),
      archivePurpose: (store) => rowSelector(store).rowProperties.archivePurpose ?? null,
      isHeaderCell: (store) =>
        !rowSelector(store).isSingleInput &&
        computeIsHeaderCell(
          rowIndex,
          columnIndex,
          tableSelector(store).props.table.headerRow,
          tableSelector(store).props.table.headerCol
        ),
      highlightClass: (store) =>
        computeRowCellColors(tableSelector(store).props.cellColors, rowIndex)?.[columnIndex],
      isLastRow: (store) =>
        computeIsLastRow(
          computeVirtualRowIndex(rowIndex, rowSelector(store).evaluatedRow[columnIndex]?.mergedBox),
          tableSelector(store).dimensions.lastIndex
        ),
      isLastColumn: (store) => computeIsLastColumn(columnIndex, tableSelector(store).dimensions.lastIndex),
      table: createCellTableSelector(tableSelector),
    },
    ({
      volatileCellModel,
      rowData,
      columnConstraint,
      isTombstoned,
      selectedColIndexMin,
      selectedColIndexMax,
      selectedColIndexActive,
      isTableLocked,
      hasActiveCell,
      archivePurpose,
      isHeaderCell,
      highlightClass,
      isLastRow,
      isLastColumn,
      table,
    }): CellModel => {
      const isCellSelected = isCellSelectedHelper({selectedColIndexMin, selectedColIndexMax}, columnIndex);
      const isCellDblSelected = columnIndex === selectedColIndexActive;
      const hasActiveEdit = hasActiveCell && columnIndex === selectedColIndexActive;
      const isCellLocked =
        isTableLocked || Boolean(volatileCellModel.evaluatedCell.locked) || archivePurpose != null;
      const cell: Cell = {
        ...volatileCellModel.cell,
        text:
          volatileCellModel.cell.text ||
          (volatileCellModel.cell.typedValue != null && isTextEditableType(volatileCellModel.cell.typedValue)
            ? formatTypedValue(volatileCellModel.cell.typedValue)
            : ''),
        // grayOutContents is only set for StructuredTables
        ...(isTombstoned && {grayOutContents: true}),
      };
      const mergedError = getErrorOrWarningFromCell(cell, volatileCellModel.evaluatedCell);
      return {
        cell,
        evaluatedCell: volatileCellModel.evaluatedCell,
        rowData,
        columnConstraint: getColumnConstraintWithRowData(columnConstraint, rowData),
        error: mergedError,
        errorState: computeErrorState(mergedError),
        isHeaderCell,
        highlightClass,
        isCellLocked,
        isCellSelected,
        isCellDblSelected,
        hasActiveEdit,
        isLastRow,
        isLastColumn,
        table,
      };
    }
  );
